import React, {useState, useEffect, Dispatch, useRef} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {Box, HStack, VStack, Stack, Text, Image, useMultiStyleConfig, StackProps, TextProps, BoxProps} from '@chakra-ui/react'
import {Button, Icon, Picture, PictureProps} from '_uiComponents'
import {ProductType} from '_types'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Navigation} from 'swiper/modules'
import type SwiperType from 'swiper'
import {useViewport} from '_customHooks'
import SizeSelector from './partials/size-selector'
import {updateCloudinarySize} from '_utils'

interface ProductTileOverlayProps {
    product: ProductType
    selectedSize?: string | null
    sizeErrorVisible: boolean
    setSizeErrorVisible: Dispatch<React.SetStateAction<boolean>>
    setSelectedSize: Dispatch<React.SetStateAction<string | null>>
    setSelectedVariant: Dispatch<React.SetStateAction<string | null>>
    addProductToCart: () => void
}

/**
 * @name ProductTileOverlay
 *
 * @component
 *
 * @description
 * Overlay rendered on top of product tile, enabled when user clicks on '+' icon button.
 *
 * @param {ProductTileOverlayProps} props - The properties for the SizeSelector component.
 * @param {ProductType} props.product - The product for which sizes are being selected.
 * @param {string | null} [props.selectedSize] - Optional selected size. Can be null if no size is selected.
 * @param {Dispatch<React.SetStateAction<string | null>>} props.setSelectedSize - Function to update the selected size.
 * @param {Dispatch<React.SetStateAction<string | null>>} props.setSelectedVariant - Function to update the selected variant.
 *
 * @returns {JSX.Element} The rendered `ProductTileOverlay` component.
 */
const ProductTileOverlay = ({
    product,
    selectedSize,
    sizeErrorVisible,
    setSizeErrorVisible,
    setSelectedSize,
    setSelectedVariant,
    addProductToCart,
}: ProductTileOverlayProps) => {
    const {isMobile} = useViewport()
    const [isColorSwatchActive, setIsColorSwatchActive] = useState<boolean>(!isMobile ? true : false)
    const [isSizeListActive, setIsSizeListActive] = useState<boolean>(true)
    const activeImageIndex = useRef<number | null>(null)
    const colorsRowWrapperRef = useRef<HTMLDivElement>(null)
    const swiperRef = useRef<{swiper: SwiperType}>(null)
    const arrowsSize = 32 * 2
    const maxColorSwatchWidth = 69

    const minSlidesToShow = 4
    const variationGroups = product?.c_variationGroupsData
    const colorsLength = variationGroups?.length || 0
    const [slidesPerView, setSlidesPerView] = useState<number | null>(null)

    const activeVariationGroup = variationGroups?.find((variation) => variation.default)
    const hasPriceRange = activeVariationGroup?.pricing?.hasPriceRange
    const selectedVariant = activeVariationGroup?.variants?.find((variant) => variant.ID === selectedSize)
    const selectedVariantPrice = selectedVariant?.pricing?.price?.formattedValue

    const selectedSizeLabel = selectedVariant?.size?.label

    const colors = variationGroups?.map((variationGroup, index) => {
        const isDefault = variationGroup.default || false
        if (isDefault) {
            activeImageIndex.current = index
        }
        return {
            id: variationGroup.ID,
            name: variationGroup.color?.label,
            image: updateCloudinarySize(variationGroup.images?.large?.[0]?.url || variationGroup.images?.url, 60),
            isDefault,
            selectable: variationGroup.selectable,
        }
    })

    const hasMultipleColors = colors && colors.length > 1
    const alreadyFocused = useRef<boolean>(false)
    const autoFocus = (element: HTMLElement | null) => {
        if (element && !alreadyFocused.current) {
            element.focus()
            alreadyFocused.current = true
        }
    }

    const styles = useMultiStyleConfig('ProductTileOverlay') as Record<string, BoxProps & StackProps & TextProps>
    const {formatMessage} = useIntl()

    const availableColorLabel = formatMessage({id: 'available.color', defaultMessage: 'Available colors'})
    const sizeButtonLabel = formatMessage({id: 'size.button.tile', defaultMessage: 'Size'})

    interface ColorVariant {
        id: string
        name: string
        image: string
        isDefault: boolean
        selectable: boolean
    }

    const renderColors = (colors: ColorVariant[], isSlider?: boolean) => {
        function sandDyTracking(color: string) {
            // Dynamic Yield change attribute Tracking
            if (typeof window !== 'undefined' && window.DY && typeof window.DY.API === 'function') {
                try {
                    window.DY.API('event', {
                        name: 'Change Attribute',
                        properties: {
                            dyType: 'change-attr-v1',
                            attributeType: 'color',
                            attributeValue: color,
                        },
                    })
                } catch (error) {
                    console.error('[DY Tracking] Errore nel tracking attribute filter:', error)
                }
            }
        }
        return colors
            ?.filter((color) => color.selectable)
            ?.map((color, index) => {
                if (isSlider) {
                    return (
                        <SwiperSlide key={`${color.id}-${index}`}>
                            <VStack
                                {...styles.colorWrapper}
                                as="button"
                                data-element-type="color-button"
                                onClick={() => {
                                    setSelectedVariant(color.id)
                                    sandDyTracking(color.name)
                                }}
                                tabIndex={0}
                                ref={index === 0 ? autoFocus : undefined}
                                aria-pressed={color.isDefault}
                            >
                                <Picture
                                    imagexs={color.image}
                                    alt={color.name}
                                    {...(styles.colorImage as PictureProps)}
                                    {...(color.isDefault ? (styles.colorImageSelected as PictureProps) : {})}
                                />
                                {color.isDefault && <Text {...styles.colorName}>{color.name}</Text>}
                            </VStack>
                        </SwiperSlide>
                    )
                }
                return (
                    <VStack
                        key={`${color.id}-${index}`}
                        as="button"
                        {...styles.colorWrapper}
                        data-element-type="color-button"
                        onClick={() => {
                            setSelectedVariant(color.id)
                            sandDyTracking(color.name)
                        }}
                        tabIndex={0}
                        ref={index === 0 ? autoFocus : undefined}
                        aria-pressed={color.isDefault}
                    >
                        <Picture
                            imagexs={color.image}
                            alt={color.name}
                            {...(styles.colorImage as PictureProps)}
                            {...(color.isDefault ? (styles.colorImageSelected as PictureProps) : {})}
                        />
                        {color.isDefault && <Text {...styles.colorName}>{color.name}</Text>}
                    </VStack>
                )
            })
    }

    const updateSlidesPerView = () => {
        if (colorsRowWrapperRef.current) {
            const width = colorsRowWrapperRef.current.clientWidth
            let calculated = Math.floor((width - arrowsSize) / maxColorSwatchWidth)

            if (calculated > colorsLength) calculated = colorsLength
            if (calculated < minSlidesToShow) calculated = minSlidesToShow
            setSlidesPerView(calculated)
        }
    }

    useEffect(() => {
        if (isColorSwatchActive && isSizeListActive && isMobile) {
            setIsSizeListActive(false)
        }
    }, [isColorSwatchActive])

    useEffect(() => {
        if (isSizeListActive && isColorSwatchActive && isMobile) {
            setIsColorSwatchActive(false)
        }
    }, [isSizeListActive])

    useEffect(() => {
        if (selectedSize !== null && isMobile) {
            setIsSizeListActive(false)
        }
    }, [selectedSize])

    useEffect(() => {
        updateSlidesPerView()

        window.addEventListener('resize', updateSlidesPerView)
        return () => window.removeEventListener('resize', updateSlidesPerView)
    }, [colorsLength])

    return (
        <VStack
            align="start"
            spacing={3}
            {...styles.wrapper}
            ref={colorsRowWrapperRef}
            data-testid="product-tile-overlay"
            id={`product-tile-overlay-${product.id}`}
        >
            {hasMultipleColors && !slidesPerView ? undefined : (
                <>
                    {/* Colors */}
                    {hasMultipleColors && isColorSwatchActive && (
                        <VStack {...styles.colorsRowWrapper} data-testid="color-selector">
                            {!isMobile && <Text {...styles.selectionTitle}>{availableColorLabel}</Text>}

                            {isMobile || (slidesPerView && colors.length <= slidesPerView) ? (
                                <HStack {...styles.colorsRow}>{renderColors(colors)}</HStack>
                            ) : (
                                <HStack {...styles.swiperWrapper}>
                                    <Stack
                                        {...styles.navigationPrev}
                                        className={`swiper-button-prev swiper-button-prev-color-selector`}
                                        cursor="pointer"
                                        data-testid="swiper-button-prev"
                                    >
                                        <Icon name="IconChevronLeft" size="sm" />
                                    </Stack>
                                    <Swiper
                                        ref={swiperRef}
                                        modules={[Navigation]}
                                        navigation={{
                                            prevEl: `.swiper-button-prev-color-selector`,
                                            nextEl: `.swiper-button-next-color-selector`,
                                        }}
                                        spaceBetween={10}
                                        onSwiper={(swiper) => {
                                            if (swiperRef.current) {
                                                swiper.update()
                                            }
                                        }}
                                        slidesPerView={slidesPerView && slidesPerView >= minSlidesToShow ? slidesPerView : minSlidesToShow}
                                    >
                                        {renderColors(colors, true)}
                                    </Swiper>
                                    <Stack
                                        {...styles.navigationNext}
                                        className={`swiper-button-next swiper-button-next-color-selector`}
                                        cursor="pointer"
                                        data-testid="swiper-button-next"
                                    >
                                        <Icon name="IconChevronRight" size="sm" />
                                    </Stack>
                                </HStack>
                            )}
                        </VStack>
                    )}

                    {/* Sizes */}
                    <SizeSelector
                        product={product}
                        selectedSize={selectedSize}
                        isSizeListActive={isSizeListActive}
                        sizeErrorVisible={sizeErrorVisible}
                        setSizeErrorVisible={setSizeErrorVisible}
                        setSelectedSize={setSelectedSize}
                        focusFirstSize={!hasMultipleColors}
                    />

                    {isMobile && (
                        <HStack {...styles.mobileTogglesRow}>
                            {colors && colors?.length > 1 && (
                                <HStack>
                                    <HStack
                                        onClick={() => setIsColorSwatchActive(!isColorSwatchActive)}
                                        {...(isColorSwatchActive ? styles.hasActiveArrow : '')}
                                    >
                                        <Image
                                            src={updateCloudinarySize(activeVariationGroup.images?.large?.[0]?.url, 48)}
                                            alt={activeVariationGroup.images?.large?.[0]?.alt}
                                            {...styles.colorImageToggler}
                                        />
                                        <Icon name="IconChevronDown" size="xs" />
                                    </HStack>
                                    <Box {...styles.separator} />
                                </HStack>
                            )}
                            <HStack onClick={() => setIsSizeListActive(!isSizeListActive)} {...(isSizeListActive ? styles.hasActiveArrow : '')}>
                                <Text {...styles.sizeLabelToggler}>{selectedSizeLabel ? selectedSizeLabel : sizeButtonLabel}</Text>
                                <Icon name="IconChevronDown" size="sm" />
                            </HStack>
                        </HStack>
                    )}

                    <Box {...styles.addToCartButtonWrapper}>
                        <HStack {...styles.addToCartRow} data-testid="add-to-cart-row">
                            {hasPriceRange && selectedVariantPrice && <Text {...styles.rangeRealPrice}>{selectedVariantPrice}</Text>}
                            <Button data-testid="add-to-cart-button" onClick={addProductToCart}>
                                {formatMessage({id: 'buy.button', defaultMessage: 'Add to cart'})}
                            </Button>
                        </HStack>

                        {sizeErrorVisible && (
                            <Box {...styles.addToCartNoSizeWrapper}>
                                <Text as="span" {...styles.addToCartNoSize}>
                                    <FormattedMessage id="error.select.size" defaultMessage="Select the size to proceed" />
                                </Text>
                            </Box>
                        )}
                    </Box>
                </>
            )}
        </VStack>
    )
}

export {ProductTileOverlay}
